import { create } from 'zustand';
import { track } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';
import { uploadFiles } from '@/features/knowledge-bases/api/knowledgeBases.api';
import {
  parseUploadResponse,
  processUploadResponseMessage,
} from '@/features/knowledge-bases/lib/parse-upload-response';
import type {
  FileUploadState,
  FileUploadStore,
  FileUploadItem,
  UploadStatus,
  FileUploadSetter,
} from './file-upload.store.types';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/lib/utils/logger';

// Initial state
const initialState: FileUploadState = {
  uploads: {},
  loading: false,
  error: null,
};

// Helper: Generate unique upload ID
function generateUploadId(): string {
  return `upload_${uuidv4()}`;
}

// Helper: Get all uploads for a knowledge base
function getUploadsForKnowledgeBase(
  uploads: Record<string, FileUploadItem | undefined>,
  knowledgeBaseId: string,
): FileUploadItem[] {
  return Object.values(uploads).filter(
    (upload): upload is FileUploadItem =>
      upload !== undefined && upload.knowledgeBaseId === knowledgeBaseId,
  );
}

// Helper: Filter uploads by predicate
function filterUploads(
  uploads: Record<string, FileUploadItem | undefined>,
  predicate: (upload: FileUploadItem) => boolean,
): Record<string, FileUploadItem | undefined> {
  return Object.fromEntries(
    Object.entries(uploads).filter(([, upload]) => upload && predicate(upload)),
  );
}

// Action Factory: Queue Management
function makeQueueActions(set: FileUploadSetter) {
  return {
    addFilesToQueue: (knowledgeBaseId: string, files: File[]): string[] => {
      const uploadIds: string[] = [];

      set((state: FileUploadStore) => {
        const newUploads = { ...state.uploads };

        files.forEach((file) => {
          const uploadId = generateUploadId();
          uploadIds.push(uploadId);

          newUploads[uploadId] = {
            id: uploadId,
            file,
            knowledgeBaseId,
            status: 'pending',
            error: null,
            startedAt: new Date(),
            abortController: new AbortController(),
          };
        });

        return { uploads: newUploads };
      });

      return uploadIds;
    },

    removeUpload: (uploadId: string) => {
      set((state: FileUploadStore) => ({
        uploads: Object.fromEntries(
          Object.entries(state.uploads).filter(([id]) => id !== uploadId),
        ),
      }));
    },

    clearCompletedUploads: () => {
      set((state: FileUploadStore) => ({
        uploads: filterUploads(
          state.uploads,
          (u) => u.status !== 'completed' && u.status !== 'failed' && u.status !== 'skipped',
        ),
      }));
    },
  };
}

// Action Factory: Status Management
function makeStatusActions(set: FileUploadSetter) {
  return {
    updateStatus: (uploadId: string, status: UploadStatus) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: { ...upload, status },
          },
        };
      });
    },

    completeUpload: (uploadId: string, documentId: string) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: {
              ...upload,
              status: 'completed',
              documentId,
              completedAt: new Date(),
              error: null,
            },
          },
        };
      });
    },

    skippedUpload: (uploadId: string, documentId: string) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: {
              ...upload,
              status: 'skipped',
              documentId,
              completedAt: new Date(),
              error: null,
            },
          },
        };
      });
    },

    failUpload: (uploadId: string, error: string) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: {
              ...upload,
              status: 'failed',
              error,
              completedAt: new Date(),
            },
          },
        };
      });
    },

    cancelUpload: (uploadId: string) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        // Abort the request if possible
        upload.abortController?.abort();

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: {
              ...upload,
              status: 'failed',
              // eslint-disable-next-line i18next/no-literal-string -- written to `FileUploadItem.error`, which the table never renders
              error: 'Upload cancelled',
              completedAt: new Date(),
            },
          },
        };
      });
    },

    // When upload fails, user should be able to retry by clicking failed file
    retryUpload: (uploadId: string) => {
      set((state: FileUploadStore) => {
        const upload = state.uploads[uploadId];
        if (!upload) return state;

        return {
          uploads: {
            ...state.uploads,
            [uploadId]: {
              ...upload,
              status: 'pending',
              error: null,
              startedAt: new Date(),
              completedAt: undefined,
              abortController: new AbortController(),
            },
          },
        };
      });
    },
  };
}

// Action Factory: Upload Operations
function makeUploadActions(set: FileUploadSetter, get: () => FileUploadStore) {
  return {
    startUpload: async (uploadId: string) => {
      const state = get();
      const upload = state.uploads[uploadId];

      if (!upload || upload.status !== 'pending') {
        return;
      }

      // Update status to uploading
      state.updateStatus(uploadId, 'uploading');

      try {
        // Upload file using existing API
        if (!upload.knowledgeBaseId) {
          throw new Error(
            i18n.t('knowledge:knowledgeBases.fileUploadStore.knowledgeBaseIdRequiredForUpload'),
          );
        }
        const response = await uploadFiles(upload.knowledgeBaseId, [upload.file]);

        const processedResponse = processUploadResponseMessage(response.message);
        parseUploadResponse(processedResponse, upload.file.name);

        // Complete upload - use filename as identifier
        const documentId = upload.file.name;
        if (processedResponse.failedCount > 0) {
          state.failUpload(
            uploadId,
            i18n.t('knowledge:knowledgeBases.fileUploadStore.failedToProcess', {
              fileName: upload.file.name,
            }),
          );
          track('kb_file_upload_failed', { kb_id: upload.knowledgeBaseId });
        } else if (processedResponse.skippedCount > 0) {
          state.skippedUpload(uploadId, documentId);
        } else {
          state.completeUpload(uploadId, documentId);
          track('kb_file_uploaded', { kb_id: upload.knowledgeBaseId });
        }
      } catch (err) {
        // eslint-disable-next-line i18next/no-literal-string -- internal fallback; the original error is what gets re-thrown
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        state.failUpload(uploadId, errorMessage);
        track('kb_file_upload_failed', { kb_id: upload.knowledgeBaseId });
        throw err; // Re-throw so hook can handle it
      }
    },

    uploadAllForKnowledgeBase: async (knowledgeBaseId: string) => {
      const state = get();
      const uploads = getUploadsForKnowledgeBase(state.uploads, knowledgeBaseId);
      const pendingUploads = uploads.filter((upload) => upload.status === 'pending');

      if (pendingUploads.length === 0) {
        return;
      }

      set({ loading: true, error: null });

      const errors: Error[] = [];

      try {
        // Upload files sequentially (to avoid overwhelming the server)
        for (const upload of pendingUploads) {
          try {
            await get().startUpload(upload.id);
          } catch (err) {
            // Collect errors but continue uploading other files
            errors.push(err instanceof Error ? err : new Error('Upload failed'));
          }
        }

        // If all uploads failed, throw
        if (errors.length === pendingUploads.length) {
          throw new Error(i18n.t('knowledge:knowledgeBases.fileUploadStore.allUploadsFailed'));
        }
      } catch (err) {
        // eslint-disable-next-line i18next/no-literal-string -- internal fallback; the original error is what gets re-thrown
        const errorMessage = err instanceof Error ? err.message : 'Bulk upload failed';
        set({ error: errorMessage });
        throw err; // Re-throw so hook can handle it
      } finally {
        set({ loading: false });
      }
    },
  };
}

// Action Factory: Reset
function makeResetActions(set: FileUploadSetter) {
  return {
    reset: () => {
      // Cancel all active uploads
      const state = useFileUploadStore.getState();
      Object.values(state.uploads).forEach((upload) => {
        upload?.abortController?.abort();
      });

      set(initialState);
    },
  };
}

// Create the store
export const useFileUploadStore = create<FileUploadStore>((set, get) => ({
  ...initialState,
  ...makeQueueActions(set),
  ...makeStatusActions(set),
  ...makeUploadActions(set, get),
  ...makeResetActions(set),
}));

// PERFORMANCE-OPTIMIZED SELECTORS

/**
 * Get uploads for a specific knowledge base
 */
export const useUploadsForKnowledgeBase = (knowledgeBaseId: string) =>
  useFileUploadStore((state) => getUploadsForKnowledgeBase(state.uploads, knowledgeBaseId));

/* *
 * Get count of uploads by status for a knowledge base
 */
export const useUploadCounts = (knowledgeBaseId: string) =>
  useFileUploadStore((state) => {
    const uploads = getUploadsForKnowledgeBase(state.uploads, knowledgeBaseId);
    return {
      total: uploads.length,
      pending: uploads.filter((u) => u.status === 'pending').length,
      uploading: uploads.filter((u) => u.status === 'uploading').length,
      processing: uploads.filter((u) => u.status === 'processing').length,
      completed: uploads.filter((u) => u.status === 'completed').length,
      failed: uploads.filter((u) => u.status === 'failed').length,
      skipped: uploads.filter((u) => u.status === 'skipped').length,
    };
  });

/**
 * Get all actions (optimized selector)
 */
export const useFileUploadActions = () =>
  useFileUploadStore((state) => ({
    addFilesToQueue: state.addFilesToQueue,
    startUpload: state.startUpload,
    uploadAllForKnowledgeBase: state.uploadAllForKnowledgeBase,
    updateStatus: state.updateStatus,
    completeUpload: state.completeUpload,
    skippedUpload: state.skippedUpload,
    failUpload: state.failUpload,
    cancelUpload: state.cancelUpload,
    removeUpload: state.removeUpload,
    clearCompletedUploads: state.clearCompletedUploads,
    reset: state.reset,
  }));

// Development logging
if (process.env.NODE_ENV === 'development') {
  useFileUploadStore.subscribe((state) => {
    const totalUploads = Object.keys(state.uploads).length;
    const activeUploads = Object.values(state.uploads).filter(
      (u) => u && (u.status === 'uploading' || u.status === 'processing'),
    ).length;

    if (totalUploads > 0) {
      logger.debug(
        `[FileUploadStore] Total: ${totalUploads} | Active: ${activeUploads} | Loading: ${state.loading}`,
      );
    }
  });
}

// Expose for debugging in development
if (import.meta.env.DEV) {
  window.useFileUploadStore = useFileUploadStore;
}
